import {
  AlertState,
  type TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';
import { createSearchParams, Link } from 'react-router-dom';

import { ActivityStatus } from '$components/device/activity-status/activity-status';
import { DoorOrLidStatus } from '$components/door-or-lid-status/door-or-lid-status';
import { searchDeviceIcon } from '$features/devices/devices.utils';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';
import { useDeviceTelemetryDataForSnapshot } from '$features/monitoring/parameters/useDeviceTelemetryDataForSnapshot';
import { UnitAlertStateV2 } from '$features/monitoring/v2/unit-alert-stateV2';

import type { DashboardSelectedDevice } from '../dashboard.types';

import { ParameterMiniChart } from './parameter-mini-chart';

interface DeviceOverviewItemProps {
  selectedDevice: DashboardSelectedDevice;
  onConfigure: () => void;
}

function latestParameter(
  telemetryId: string,
  telemetry: TelemetryHistoryEnrichedDTO[],
): TelemetryHistoryEnrichedDTO | undefined {
  return telemetry
    .filter((parameter) => parameter.telemetryId === telemetryId)
    .sort(
      (left, right) => new Date(right.sentAt).getTime() - new Date(left.sentAt).getTime(),
    )
    .at(0);
}

export function DeviceOverviewItem({
  selectedDevice,
  onConfigure,
}: DeviceOverviewItemProps) {
  const { t } = useTranslation();
  const snapshotQuery = useDeviceSnapshot({
    manufacturer: selectedDevice.manufacturer,
    serialNumber: selectedDevice.serialNumber,
  });
  const configuredTelemetry = useDeviceTelemetryDataForSnapshot(snapshotQuery.data);

  if (snapshotQuery.isLoading) {
    return (
      <article className="device-overview-item">
        <div className="device-overview-item__identity">
          <strong>{selectedDevice.serialNumber}</strong>
        </div>
        <div className="device-overview-item__feedback">
          <DotsLoader />
        </div>
      </article>
    );
  }

  if (snapshotQuery.isError || !snapshotQuery.data) {
    return (
      <article className="device-overview-item">
        <div className="device-overview-item__identity">
          <strong>{selectedDevice.serialNumber}</strong>
        </div>
        <div className="device-overview-item__feedback">
          <span>{t('dashboard.deviceOverview.deviceLoadError')}</span>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => {
              snapshotQuery.refetch();
            }}
          >
            {t('dashboard.deviceOverview.retry')}
          </Button>
        </div>
      </article>
    );
  }

  const { inventoryDevice, currentShadow, latestTelemetry } = snapshotQuery.data;
  const allConfiguredTelemetry = [
    ...configuredTelemetry.primaryParameters,
    ...configuredTelemetry.secondaryParameters,
  ];
  const location = [
    inventoryDevice.location?.site,
    inventoryDevice.location?.building,
    inventoryDevice.location?.room,
    inventoryDevice.location?.position,
  ]
    .filter(Boolean)
    .join(', ');
  const monitoringSearch = createSearchParams({
    sidecarRoute: 'monitoring/parameters',
    manufacturer: selectedDevice.manufacturer,
    serialNumber: selectedDevice.serialNumber,
  }).toString();
  const parameters = selectedDevice.telemetryIds.map((telemetryId) => {
    const configuredParameter = latestParameter(telemetryId, allConfiguredTelemetry);
    const rawParameter = latestParameter(telemetryId, latestTelemetry);
    if (configuredParameter) return configuredParameter;
    if (rawParameter) return { ...rawParameter, name: rawParameter.name || telemetryId };
    return undefined;
  });
  const alertState = currentShadow.aggregateAlertState;
  const hasKnownAlertState = Object.values(AlertState).includes(alertState as AlertState);
  const deviceName = inventoryDevice.name || inventoryDevice.serialNumber;

  return (
    <article className="device-overview-item">
      <div className="device-overview-item__header">
        <div className="device-overview-item__identity">
          <div className="device-overview-item__identity-header">
            <Icon
              name={searchDeviceIcon(
                inventoryDevice.type,
                undefined,
                inventoryDevice.model,
              )}
              size={IconSizeClasses.Small}
            />
            <div>
              <h4>{deviceName}</h4>
              <span>
                {inventoryDevice.serialNumber} |{' '}
                {location || t('dashboard.deviceOverview.noLocation')}
              </span>
            </div>
          </div>
        </div>
        <div className="device-overview-item__statuses">
          <ActivityStatus
            activityStatus={currentShadow.activityState || null}
            bordered={false}
            size="small"
          />
          <UnitAlertStateV2
            alertState={hasKnownAlertState ? (alertState as AlertState) : undefined}
            side="top"
          />
          <DoorOrLidStatus
            state={currentShadow.lidState || currentShadow.doorState || undefined}
            subject={currentShadow.lidState ? 'lid' : 'door'}
            fallback=""
          />
        </div>
        <Tooltip
          side="top"
          trigger={
            <Link
              to={{ search: `?${monitoringSearch}` }}
              className="device-overview-item__link"
              aria-label={t('dashboard.deviceOverview.viewDevice', { deviceName })}
            >
              <span>{t('dashboard.deviceOverview.monitoringAction')}</span>
              <Icon name="right" size={IconSizeClasses.XSmall} />
            </Link>
          }
        >
          {t('dashboard.deviceOverview.monitoringHint', { deviceName })}
        </Tooltip>
      </div>

      {selectedDevice.telemetryIds.length === 0 ? (
        <div className="device-overview-item__no-parameters">
          <span>{t('dashboard.deviceOverview.noParameters')}</span>
          <Button type="button" variant="tertiary" size="small" onClick={onConfigure}>
            {t('dashboard.deviceOverview.configure')}
          </Button>
        </div>
      ) : (
        <div className="device-overview-item__parameters">
          {selectedDevice.telemetryIds.map((telemetryId, index) => (
            <ParameterMiniChart
              key={telemetryId}
              manufacturer={selectedDevice.manufacturer}
              serialNumber={selectedDevice.serialNumber}
              telemetryId={telemetryId}
              parameter={parameters.at(index)}
              alertState={
                currentShadow.components.find((component) => component.id === telemetryId)
                  ?.alertState
              }
            />
          ))}
        </div>
      )}
    </article>
  );
}
